import React, { useEffect, useMemo, useState } from 'react'
import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  List,
  SvgIcon,
  Typography,
  Pagination,
  Box,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import T from 'T'
import { Clock } from '@styled-icons/heroicons-outline/Clock'
import get from 'lodash/get'
import capitalize from 'lodash/capitalize'
import { formatDateToFEFormatDateFns } from 'utils/date'
import { PAYMENT_METHOD_IMAGES, PAYMENT_METHODS } from 'settings/constants/payment'
import { useLazyGetTransactionListQuery } from 'api/payment-gateway/paymentGateway'
import { getPaymentMethod, getPaymentMethodImage } from 'utils/payment'
import PaymentListItem from 'containers/payments/PaymentListItem'
import { shallowEqual, useSelector } from 'react-redux'
import { downloadPaymentReceiptBlob } from 'utils/file'

import { INITIAL_PAGE, PAGE_SIZE } from 'utils/pagination'
import { PAGINATION } from 'settings/constants/pagination'
import { handleError } from 'utils/error'
import HHTablePagination from 'components/common/HHTablePagination'
import PaymentHistorySkeleton from 'components/payments/PaymentHistorySkeleton'

const { CHECK, OTHER, ACCOUNT_CREDIT, ACH, CREDIT_CARD } = PAYMENT_METHODS
const { ROWS_PER_PAGE_OPTIONS } = PAGINATION

const getDate = date => {
  if (date) {
    return formatDateToFEFormatDateFns(new Date(date))
  }
  return 'NA'
}

const PaymentHistory = () => {
  const [getTransactionList, { data: transactionsData, isFetching: isFetchingTransactions }] = useLazyGetTransactionListQuery()
  const [pagination, setPagination] = useState({ page: INITIAL_PAGE, rowsPerPage: PAGE_SIZE })
  const { page, rowsPerPage } = pagination
  const businessId = useSelector(state => get(state, 'LoginPersist.selectedLoginDetails.businessId'), shallowEqual)
  const accountId = useSelector(state => get(state, 'LoginPersist.selectedLoginDetails.accountId'), shallowEqual)
  const theme = useTheme()
  const isLg = useMediaQuery(theme.breakpoints.up('md'))

  const getAllTransactions = (requestedPage = page, requestedPageSize = rowsPerPage) => {
    getTransactionList({ businessId, payload: { accountId, requestedPage, requestedPageSize } }).unwrap().catch(handleError)
  }
  const totalItemCount = useMemo(() => get(transactionsData, 'totalItems', 0), [transactionsData])
  const totalPageCount = useMemo(() => get(transactionsData, 'totalPages', 0), [transactionsData])
  const transactionList = useMemo(() => get(transactionsData, 'transactions', []), [transactionsData])

  const handlePageChange = (newPage = INITIAL_PAGE) => setPagination(p => ({ ...p, page: newPage }))

  const handleRowsPerPageChange = event => {
    setPagination({ page: INITIAL_PAGE, rowsPerPage: event.target.value })
  }

  useEffect(() => {
    if (businessId && accountId) {
      getAllTransactions()
    }
  }, [businessId, accountId, page, rowsPerPage])

  const isLoading = isFetchingTransactions && !transactionsData
  if (isLoading) {
    return (
      <>
        <PaymentHistorySkeleton />
      </>
    )
  }

  return (
    <>
      <Card elevation={0} variant="outlined" sx={{ width: '100%' }}>
        <CardHeader
          avatar={
            <SvgIcon>
              <Clock />
            </SvgIcon>
          }
          title={
            <Typography fontWeight={600} variant="h6">
              {T.PAYMENT_HISTORY}
            </Typography>
          }
        />
        <Divider />
        <CardContent sx={{ padding: 0 }}>
          <List disablePadding>
            {transactionList.map((transaction, i) => {
              const {
                transactionStatus = '',
                paymentMethod,
                accountType,
                accountNumber,
                transactionAmountCents,
                surchargeAmountCents,
                paymentDate,
                checkNum,
                paymentNumber,
                accountTransactionHistoryId,
                accountId: receiptAccountId,
              } = transaction
              const capitalizeTransactionStatus = capitalize(transactionStatus)
              const date = getDate(paymentDate)
              const isCheckOther = [CHECK, OTHER].includes(paymentMethod)
              const isAccountCredit = paymentMethod === ACCOUNT_CREDIT
              const paymentMethodName = paymentMethod === ACH ? paymentMethod : capitalize(getPaymentMethod(paymentMethod))
              const paymentMethodImage =
                paymentMethod === CREDIT_CARD ? getPaymentMethodImage(accountType) : PAYMENT_METHOD_IMAGES[paymentMethod]
              const isNotLast = transactionList.length - 1 !== i
              const checkValue = isCheckOther ? checkNum : accountNumber

              const handleDownloadPaymentReceipt = () => {
                if (!businessId || !receiptAccountId || !paymentNumber || !accountTransactionHistoryId) return
                downloadPaymentReceiptBlob(businessId, receiptAccountId, paymentNumber, accountTransactionHistoryId, true)
              }
              return (
                <PaymentListItem
                  accountNumber={accountNumber}
                  accountType={accountType}
                  isCheckOther={isCheckOther}
                  capitalizeTransactionStatus={capitalizeTransactionStatus}
                  checkValue={checkValue}
                  date={date}
                  isAccountCredit={isAccountCredit}
                  isNotLast={isNotLast}
                  paymentMethodImage={paymentMethodImage}
                  paymentMethodName={paymentMethodName}
                  transactionAmountCents={transactionAmountCents}
                  surchargeAmountCents={surchargeAmountCents}
                  paymentNumber={paymentNumber}
                  onClick={() => handleDownloadPaymentReceipt()}
                />
              )
            })}
          </List>
          <Divider />
          {totalItemCount > 0 && (
            <Grid container sx={{ width: '100%' }} flexWrap="nowrap" justifyContent="space-between" alignItems="center">
              <Grid container justifyContent="flex-start" pl={2} sx>
                <HHTablePagination
                  page={page}
                  component="div"
                  count={totalItemCount}
                  rowsPerPage={rowsPerPage}
                  onChangePage={(event, newPage) => handlePageChange(newPage)}
                  rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  ActionsComponent={isLg ? params => <Box display="none" {...params} /> : undefined}
                />
              </Grid>
              <Grid container justifyContent="flex-end" sx>
                {/* Pagination starts from 1 */}
                <Pagination
                  color="primary"
                  count={totalPageCount}
                  page={page + 1}
                  onChange={(event, newPage) => handlePageChange(newPage - 1)}
                />
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>
    </>
  )
}

PaymentHistory.propTypes = {}

export default PaymentHistory
